import { useState } from "react";
import { Check, Loader2, Pencil, Trash2, X } from "lucide-react";
import { ConfirmDelete } from "./ConfirmDelete";
import { CopyNameButton } from "./CopyNameButton";
import { changedFields, primaryLine, secondaryLine } from "../lib/doorGuests";

const input = "h-10 min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-700 sm:text-sm";

const EditRow = ({ guest, onSave, onCancel }) => {
  const [form, setForm] = useState({ identifier: guest.identifier, display_name: guest.display_name ?? "", first_name: guest.first_name ?? "", last_name: guest.last_name ?? "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!form.identifier.trim()) return;
    const changes = changedFields(guest, form);
    if (!Object.keys(changes).length) return onCancel();
    setBusy(true);
    await onSave(changes);
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <input data-testid="door-admin-edit-identifier" className={`${input} sm:flex-[2]`} maxLength={200} value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} />
      <div className="flex min-w-0 flex-col sm:flex-[2]">
        <input data-testid="door-admin-edit-display" className={input} maxLength={200} placeholder="Display name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
        <CopyNameButton testid="door-admin-copy-display-name" display={form.display_name} first={form.first_name} onCopy={(v) => setForm((f) => ({ ...f, first_name: v }))} />
      </div>
      <input data-testid="door-admin-edit-first" className={`${input} sm:flex-1`} maxLength={80} placeholder="First" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
      <input data-testid="door-admin-edit-last" className={`${input} sm:flex-1`} maxLength={80} placeholder="Last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
      <div className="flex gap-1">
        <button type="submit" data-testid="door-admin-edit-save" disabled={busy || !form.identifier.trim()} aria-label="Save" className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-700 text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</button>
        <button type="button" data-testid="door-admin-edit-cancel" onClick={onCancel} aria-label="Cancel" className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-500"><X className="h-4 w-4" /></button>
      </div>
    </form>
  );
};

export const DoorGuestAdminRow = ({ guest, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const second = secondaryLine(guest);
  return (
    <li data-testid={`door-admin-row-${guest.id}`} className="border-b border-slate-100 px-4 py-3 last:border-0">
      {editing ? (
        <EditRow guest={guest} onCancel={() => setEditing(false)} onSave={async (form) => { if (await onSave(guest, form)) setEditing(false); }} />
      ) : (
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p data-testid="door-admin-primary" className="break-words font-semibold text-slate-900">{primaryLine(guest)}</p>
            {second && <p data-testid="door-admin-secondary" className="break-words text-sm text-slate-500">{second}</p>}
          </div>
          {guest.checked_in && <span data-testid="door-admin-checked-in" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700"><Check className="h-3.5 w-3.5" /> Checked in</span>}
          <button type="button" data-testid="door-admin-edit-btn" onClick={() => setEditing(true)} aria-label="Edit guest" className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
          <ConfirmDelete title={`Remove ${guest.identifier} from the guest list?`} onConfirm={() => onDelete(guest)} testid="door-admin-delete">
            <button type="button" data-testid="door-admin-delete-btn" aria-label="Remove guest" className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </ConfirmDelete>
        </div>
      )}
    </li>
  );
};
